// Stocky が公式 CSV を提供していない仕入先系データの、手作り/別システム CSV
// fallback。新 SPA が旧 backend の unsafe semantics を呼ばないよう、v2 URL だけを
// このモジュールへ閉じ込める。旧 /suppliers・/supplier-links へは fallback しない。
import { apiFetch } from "./client";
import type {
  CsvApplyResultBase,
  CsvMapping,
  CsvPreview,
} from "./stocky_csv_import";

const MANUAL_SUPPLIERS_V2 = "/api/migrate/csv/manual-suppliers-v2";
const MANUAL_SUPPLIER_LINKS_V2 =
  "/api/migrate/csv/manual-supplier-links-v2";

export interface ManualSupplierCsvResult extends CsvApplyResultBase {
  valid_rows: number;
  skipped_row_count: number;
  created: number;
  updated: number;
  unchanged: number;
  ambiguous_names: string[];
  ambiguous_name_count: number;
}

export interface ManualSupplierLinkCsvResult extends CsvApplyResultBase {
  valid_rows: number;
  skipped_row_count: number;
  linked_variants: number;
  links_created: number;
  links_updated: number;
  links_unchanged: number;
  suppliers_created: number;
  conditions_written: number;
  condition_values_overwritten: number;
  preferred_created: number;
  preferred_changed: number;
  preferred_preserved: number;
  unmatched_skus: string[];
  ambiguous_skus: string[];
  ambiguous_sku_count: number;
  ambiguous_supplier_names: string[];
  ambiguous_supplier_name_count: number;
}

interface ApplyOptions {
  dryRun: boolean;
  expectedPlanToken?: string;
  confirmRetentionOnly?: boolean;
  sourceImportId: string;
  sourceFileName: string;
  sourceMediaType: string;
}

function preview(
  path: string,
  fileBase64: string,
): Promise<CsvPreview> {
  return apiFetch<CsvPreview>(`${path}/preview`, {
    method: "POST",
    body: { file_base64: fileBase64 },
  });
}

function apply<TResult extends CsvApplyResultBase>(
  path: string,
  fileBase64: string,
  mapping: CsvMapping,
  options: ApplyOptions,
): Promise<TResult> {
  return apiFetch<TResult>(`${path}/apply`, {
    method: "POST",
    body: {
      file_base64: fileBase64,
      mapping,
      dry_run: options.dryRun,
      source_import_id: options.sourceImportId,
      source_file_name: options.sourceFileName,
      source_media_type: options.sourceMediaType,
      // plan token は内容確認の証拠であって、「正規化しない」同意の
      // 代用ではない。checkbox の別操作を boolean で必ず束縛する。
      confirm_retention_only: options.confirmRetentionOnly ?? false,
      ...(options.expectedPlanToken === undefined
        ? {}
        : { expected_plan_token: options.expectedPlanToken }),
    },
  });
}

export function previewManualSupplierCsv(
  fileBase64: string,
): Promise<CsvPreview> {
  return preview(MANUAL_SUPPLIERS_V2, fileBase64);
}

export function applyManualSupplierCsv(
  fileBase64: string,
  mapping: CsvMapping,
  options: ApplyOptions,
): Promise<ManualSupplierCsvResult> {
  return apply<ManualSupplierCsvResult>(
    MANUAL_SUPPLIERS_V2,
    fileBase64,
    mapping,
    options,
  );
}

export function previewManualSupplierLinkCsv(
  fileBase64: string,
): Promise<CsvPreview> {
  return preview(MANUAL_SUPPLIER_LINKS_V2, fileBase64);
}

export function applyManualSupplierLinkCsv(
  fileBase64: string,
  mapping: CsvMapping,
  options: ApplyOptions,
): Promise<ManualSupplierLinkCsvResult> {
  return apply<ManualSupplierLinkCsvResult>(
    MANUAL_SUPPLIER_LINKS_V2,
    fileBase64,
    mapping,
    options,
  );
}
